import type {ArtifactVersionMetricDataState} from './artifactMetricData';
import {hasArtifactVersionMetricName} from './artifactMetricData';
import {
  type ArtifactVersionMetricBar,
  type ArtifactVersionMetricPlotPoint,
  toArtifactVersionMetricBars,
  toArtifactVersionMetricLineSegments,
  toArtifactVersionMetricPlotPoints,
  toArtifactVersionMetricScatterPoints,
} from './artifactVersionMetricAdapters';
import {
  getArtifactVersionMetricChartTitle,
  getArtifactVersionMetricScatterXDomain,
  getArtifactVersionMetricXDomain,
  getArtifactVersionMetricYDomainFromValues,
  isArtifactVersionMetricDualMetricScatter,
} from './artifactVersionMetricChartUtils';
import type {
  ArtifactVersionMetricChartRow,
  ArtifactVersionMetricPanelConfig,
} from './types';

export type ArtifactVersionMetricPlotPlaceholder = {
  kind: 'placeholder';
  message: string;
  onRetry?: () => void;
};

export type ArtifactVersionMetricPlotReady = {
  kind: 'ready';
  config: ArtifactVersionMetricPanelConfig;
  title: string;
  rows: ArtifactVersionMetricChartRow[];
  points: ArtifactVersionMetricPlotPoint[];
  lineSegments: Array<Array<{x: number; y: number}>>;
  bars: ArtifactVersionMetricBar[];
  xDomain: [number, number] | null;
  yDomain: [number, number];
  hasPartialData: boolean;
  partialDataMessage?: string;
};

export type ArtifactVersionMetricPlotModel =
  | ArtifactVersionMetricPlotPlaceholder
  | ArtifactVersionMetricPlotReady;

type BuildArtifactVersionMetricPlotModelArgs = {
  config: ArtifactVersionMetricPanelConfig;
  hasSelection: boolean;
  yMetricData: ArtifactVersionMetricDataState;
  xMetricData: ArtifactVersionMetricDataState;
};

function getPartialDataMessage(
  config: ArtifactVersionMetricPanelConfig,
  hasPartialData: boolean
): string | undefined {
  if (!hasPartialData) {
    return undefined;
  }
  if (isArtifactVersionMetricDualMetricScatter(config)) {
    return 'Some selected versions are missing a value for one of the metrics.';
  }
  return 'Some selected versions have no value for this metric at their logged step.';
}

function buildReadyPlot({
  config,
  rows,
  points,
  bars,
  lineSegments,
  xDomain,
  yDomain,
  hasPartialData,
}: {
  config: ArtifactVersionMetricPanelConfig;
  rows: ArtifactVersionMetricChartRow[];
  points: ArtifactVersionMetricPlotPoint[];
  lineSegments: Array<Array<{x: number; y: number}>>;
  bars: ArtifactVersionMetricBar[];
  xDomain: [number, number] | null;
  yDomain: [number, number];
  hasPartialData: boolean;
}): ArtifactVersionMetricPlotReady {
  const title = getArtifactVersionMetricChartTitle(config);
  return {
    kind: 'ready',
    config,
    title: title ?? '',
    rows,
    points,
    lineSegments,
    bars,
    xDomain,
    yDomain,
    hasPartialData,
    partialDataMessage: getPartialDataMessage(config, hasPartialData),
  };
}

export function buildArtifactVersionMetricPlotModel({
  config,
  hasSelection,
  yMetricData,
  xMetricData,
}: BuildArtifactVersionMetricPlotModelArgs): ArtifactVersionMetricPlotModel {
  const hasMetric = hasArtifactVersionMetricName(config.metricName);
  const isScatter = config.chartType === 'scatter';
  const hasScatterXMetric = hasArtifactVersionMetricName(config.xMetricName);
  const isDualMetricScatter = isArtifactVersionMetricDualMetricScatter(config);
  const loading =
    yMetricData.loading || (isDualMetricScatter && xMetricData.loading);
  const error = yMetricData.error ?? xMetricData.error;

  if (config.chartType == null) {
    return {
      kind: 'placeholder',
      message: 'Configure metric',
    };
  }
  if (isScatter && (!hasMetric || !hasScatterXMetric)) {
    return {
      kind: 'placeholder',
      message: 'Configure X & Y axis metrics',
    };
  }
  if (!hasMetric) {
    return {kind: 'placeholder', message: 'Configure Y axis metric'};
  }
  if (!hasSelection) {
    return {
      kind: 'placeholder',
      message: 'Select artifact versions to compare.',
    };
  }
  const hasRenderableLoadingRows =
    yMetricData.rows.length > 0 &&
    (!isDualMetricScatter || xMetricData.rows.length > 0);
  if (loading && !hasRenderableLoadingRows) {
    return {
      kind: 'placeholder',
      message: 'Loading artifact version metric values.',
    };
  }
  if (error != null) {
    return {
      kind: 'placeholder',
      message: 'Metric values could not be loaded.',
      onRetry:
        yMetricData.error != null ? yMetricData.retry : xMetricData.retry,
    };
  }

  if (config.chartType === 'bar') {
    const rows = yMetricData.rows;
    const bars = toArtifactVersionMetricBars(rows);
    if (bars.length === 0) {
      return {
        kind: 'placeholder',
        message: 'No values found for this metric.',
      };
    }
    const yValues = bars.map(bar => bar.value);
    return buildReadyPlot({
      config,
      rows,
      points: [],
      lineSegments: [],
      bars,
      xDomain: null,
      yDomain: getArtifactVersionMetricYDomainFromValues(yValues, config),
      hasPartialData: yMetricData.hasPartialData,
    });
  }

  if (isDualMetricScatter) {
    const points = toArtifactVersionMetricScatterPoints(
      yMetricData.rows,
      xMetricData.rows
    );
    if (points.length === 0) {
      return {
        kind: 'placeholder',
        message: 'No values found for this metric.',
      };
    }
    const rows = points.map(point => point.artifactRow);
    const yValues = points.map(point => point.y);
    const xValues = points.map(point => point.x);
    return buildReadyPlot({
      config,
      rows,
      points,
      lineSegments: [],
      bars: [],
      xDomain: getArtifactVersionMetricScatterXDomain(xValues, config),
      yDomain: getArtifactVersionMetricYDomainFromValues(yValues, config),
      hasPartialData: yMetricData.hasPartialData || xMetricData.hasPartialData,
    });
  }

  if (config.chartType === 'line') {
    const points = toArtifactVersionMetricPlotPoints(yMetricData.rows);
    if (points.length === 0) {
      return {
        kind: 'placeholder',
        message: 'No values found for this metric.',
      };
    }
    const yValues = points.map(point => point.y);
    return buildReadyPlot({
      config,
      rows: yMetricData.rows,
      points,
      lineSegments: toArtifactVersionMetricLineSegments(yMetricData.rows),
      bars: [],
      xDomain: getArtifactVersionMetricXDomain(yMetricData.rows),
      yDomain: getArtifactVersionMetricYDomainFromValues(yValues, config),
      hasPartialData: yMetricData.hasPartialData,
    });
  }

  return {
    kind: 'placeholder',
    message: 'Artifact version metric chart placeholder.',
  };
}
